import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import Icon from '../components/Icon'
import { useStudentsStore } from '../../stores/studentsStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { useClassStore } from '../../stores/classesStore'
import { useAuth } from '../../contexts/AuthContext'
import {
  Class,
  Student,
  Permission,
  Accommodation,
  School,
} from '../../types'

const Students: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { students, loading, error, fetchStudents, removeStudent } =
    useStudentsStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()
  const { schoolClasses, fetchSchoolClasses } = useClassStore()

  const [isDeletingAll, setIsDeletingAll] = React.useState(false)

  // Fetch students, schools, and schoolClasses on component mount
  useEffect(() => {
    fetchStudents()
    fetchSchools()
    fetchSchoolClasses()
  }, [fetchStudents, fetchSchools, fetchSchoolClasses])

  const handleDeleteStudent = async (
    id: string,
    firstName: string,
    lastName: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${firstName} ${lastName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeStudent(id)
        await fetchStudents()
      } catch (err) {
        console.error('Error deleting student:', err)
      }
    }
  }

  const getSchoolName = (schoolId: string) => {
    const school = schools.find((school: School) => school._id === schoolId)
    return school?.displayName || 'Unknown School'
  }

  // Helper function to count how many classes a student is enrolled in
  const getStudentClassCount = (studentId: string) => {
    return schoolClasses.filter((cls: Class) => {
      return cls.students.some((student) => {
        // Handle both string IDs and populated student objects
        const id = typeof student === 'string' ? student : student._id
        return id === studentId
      })
    }).length
  }

  // Handle bulk delete
  const handleDeleteAll = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete all ${students.length} students? This action cannot be undone and will remove all students from classes and delete all associated accommodation records.`,
    )

    if (!confirmed) return

    setIsDeletingAll(true)

    try {
      // Delete all students one by one
      const deletePromises = students.map((student) =>
        removeStudent(student._id),
      )
      await Promise.all(deletePromises)
      await fetchStudents()
    } catch (err) {
      console.error('Error deleting all students:', err)
    } finally {
      setIsDeletingAll(false)
    }
  }

  // Check if user has permission to create students
  const canCreateStudents = user?.permissions.includes(
    Permission.MANAGE_STUDENTS,
  )

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<Student>[]>(
    () => [
      {
        accessorKey: 'studentId',
        header: 'Student ID',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {(info.getValue() as string) || 'Not assigned'}
          </div>
        ),
      },
      {
        accessorKey: 'firstName',
        header: 'Name',
        cell: (info) => {
          const student = info.row.original
          return (
            <div className="text-sm font-medium text-gray-900">
              {student.firstName} {student.lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'grade',
        header: 'Grade',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'school',
        header: 'School',
        cell: (info) => {
          const school = info.getValue() as any
          const schoolId = typeof school === 'object' ? school._id : school
          return (
            <div className="text-sm text-gray-900">
              {getSchoolName(schoolId)}
            </div>
          )
        },
      },
      {
        accessorKey: 'caseManager',
        header: 'Case Manager',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {(info.getValue() as string) || 'Not assigned'}
          </div>
        ),
      },
      {
        accessorKey: 'accommodations',
        header: 'Accommodations',
        cell: (info) => {
          const accommodations = info.getValue() as Accommodation[]
          return (
            <div
              className="text-sm text-gray-500"
              title={accommodations.join(', ')}
            >
              {accommodations.length}
            </div>
          )
        },
      },
      {
        id: 'classCount',
        header: 'Classes',
        cell: ({ row }) => {
          const student = row.original
          const classCount = getStudentClassCount(student._id)
          return (
            <div className="text-sm text-gray-500">
              {classCount === 0
                ? 'No classes'
                : classCount === 1
                  ? '1 class'
                  : `${classCount} classes`}
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium flex">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/manage/students/${row.original._id}/general`)
              }}
              variant="link"
              className="mr-3"
            >
              Edit
            </Button>
            {canCreateStudents && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteStudent(
                    row.original._id,
                    row.original.firstName,
                    row.original.lastName,
                  )
                }}
                variant="link"
                className="text-red-600 hover:text-red-900"
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [schools, schoolClasses],
  )

  if (loading || schoolsLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Students</h1>
          <p className="text-gray-600">Manage students in your system</p>
        </div>
        {canCreateStudents && (
          <div className="flex gap-2 ml-auto">
            {students.length > 0 && (
              <Button
                onClick={handleDeleteAll}
                disabled={isDeletingAll}
                variant="danger"
                className="flex items-center gap-2"
              >
                {isDeletingAll && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <Icon type="trash" className="w-5 h-5" />
                {isDeletingAll ? 'Deleting...' : 'Delete All'}
              </Button>
            )}
            <Button
              onClick={() => navigate('/manage/students/new')}
              variant="primary"
              className="flex items-center gap-2"
            >
              <Icon type="plus" className="w-5 h-5" />
              Add Student
            </Button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Students Table */}
      <DataTable
        data={students}
        columns={columns}
        searchPlaceholder="Search students..."
        emptyStateTitle="No students"
        emptyStateDescription="Get started by adding your first student."
        emptyStateIcon={
          <Icon type="users" className="mx-auto h-12 w-12 text-gray-400" />
        }
        onCreateClick={
          canCreateStudents ? () => navigate('/manage/students/new') : undefined
        }
        createButtonText="Add Student"
        showCreateButton={canCreateStudents}
        loading={loading || schoolsLoading}
        onRowClick={(studentItem) =>
          navigate(`/manage/students/${studentItem._id}/general`)
        }
      />
    </div>
  )
}

export default Students
